import { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { chatApi, streamMessage } from "../api";
import type { ChatCsv, ChatImage, ChatMessage, DataSource, ThinkingStep, Validation } from "../types/chat";

type SendArgs = { message: string; images?: ChatImage[]; csvs?: ChatCsv[]; datasourceKeys?: string[] };

type LoadedMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  sql_generated?: string | null;
  sql_datasource?: string | null;
  data_sources?: DataSource[] | null;
  validation?: Validation | null;
  created_at?: string;
};

export function useChatStream(conversationIdParam: string | null, teammateParam: string | null = null) {
  const [, setSearchParams] = useSearchParams();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(conversationIdParam || null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinkingSteps, setThinkingSteps] = useState<ThinkingStep[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [datasourceKeys, setDatasourceKeys] = useState<string[] | null>(null);
  const [teammateId, setTeammateId] = useState<string | null>(conversationIdParam ? null : teammateParam);

  const abortRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    return () => { abortRef.current?.(); };
  }, []);

  useEffect(() => {
    if (!conversationIdParam) {
      setMessages([]);
      setConversationId(null);
      setDatasourceKeys(null);
      setTeammateId(teammateParam);
      return;
    }
    setConversationId(conversationIdParam);
    chatApi.getConversation(conversationIdParam)
      .then((res) => {
        setDatasourceKeys(res.data.datasource_keys ?? null);
        setTeammateId(res.data.teammate_id ?? null);
        const msgs: LoadedMessage[] = res.data.messages || [];
        setMessages(msgs.map((m) => ({
          id: m.id,
          role: m.role,
          content: m.content,
          sql: m.sql_generated ?? null,
          sql_datasource: m.sql_datasource ?? null,
          sources: m.data_sources ?? null,
          validation: m.validation ?? null,
          createdAt: m.created_at,
        })));
      })
      .catch(() => setError("Failed to load conversation"));
  }, [conversationIdParam, teammateParam]);

  const send = useCallback(({ message, images = [], csvs = [], datasourceKeys: selectedKeys }: SendArgs) => {
    const msg = message.trim();
    if ((!msg && !images.length && !csvs.length) || isStreaming) return;
    setError(null);
    setThinkingSteps([]);

    const tempUserId = `temp-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempUserId, role: "user", content: msg, images, csvs, createdAt: new Date().toISOString() }]);

    const tempAiId = `ai-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempAiId, role: "assistant", content: "", sql: null, pbi_embed: null, comparison: null, _streaming: true, createdAt: new Date().toISOString() }]);
    setIsStreaming(true);

    abortRef.current = streamMessage({
      message: msg,
      conversationId,
      images,
      csvs,
      datasourceKeys: selectedKeys,
      teammateId,
      onThinking(data) {
        setThinkingSteps((prev) => [...prev, data]);
      },
      onDelta(chunk) {
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempAiId ? { ...m, content: m.content + chunk } : m
          )
        );
      },
      onDone(meta) {
        setConversationId(meta.conversation_id);
        if (meta.datasource_keys) setDatasourceKeys(meta.datasource_keys);
        setSearchParams({ c: meta.conversation_id }, { replace: true });
        setMessages((prev) =>
          prev.map((m) =>
            m.id === tempAiId
              ? {
                  ...m,
                  id: meta.message_id,
                  sql: meta.sql_generated,
                  sql_datasource: meta.sql_datasource ?? null,
                  sources: meta.data_sources ?? null,
                  validation: meta.validation ?? null,
                  pbi_embed: meta.pbi_embed ?? null,
                  comparison: meta.comparison ?? null,
                  tokens_input: meta.tokens_input ?? 0,
                  tokens_output: meta.tokens_output ?? 0,
                  _streaming: false,
                }
              : m
          )
        );
        setThinkingSteps([]);
        setIsStreaming(false);
        window.dispatchEvent(new CustomEvent("conversation-updated"));
      },
      onError(errMsg) {
        setError(errMsg);
        setMessages((prev) => prev.filter((m) => m.id !== tempAiId));
        setThinkingSteps([]);
        setIsStreaming(false);
      },
    });
  }, [conversationId, isStreaming, setSearchParams, teammateId]);

  return { messages, conversationId, datasourceKeys, teammateId, isStreaming, thinkingSteps, error, send };
}
